import { MapEngine } from '../../map/mapEngine.js'
import { LayerType } from '../../map/layerManager.js'
import { geoAnalyzer } from '../../analysis/geoAnalyzer.js'
import { templateEngine } from '../../template/templateEngine.js'
import { i18n } from '../../i18n/i18n.js'
import { configStore } from '../../data/configStore.js'
import { regionStore } from '../../data/regionStore.js'
import { nodeStore } from '../../data/nodeStore.js'
import { veinStore } from '../../data/veinStore.js'
import { AnalysisPanel } from '../components/analysisPanel.js'
import { LayerControls } from '../components/layerControls.js'
import { LoadingState } from '../components/loadingState.js'
import { LeadFormModal } from '../components/leadFormModal.js'
import { LanguageSwitcher } from '../components/languageSwitcher.js'

class Analytics {
  constructor() {
    this.enabled = false
    this.consentGiven = false
  }

  init() {
    this.consentGiven = localStorage.getItem('fsm_analytics_consent') === 'true'
    this.enabled = this.consentGiven
  }

  trackMapClick(data) {
    if (!this.enabled) return
    if (window.plausible) {
      window.plausible('map:click', { props: data })
    }
  }

  trackBookingClick(data) {
    if (!this.enabled) return
    if (window.plausible) {
      window.plausible('booking:click', { props: data })
    }
  }
}

export class MapPage {
  constructor(containerId) {
    this.container = document.getElementById(containerId)
    this.mapEngine = null
    this.analysisPanel = null
    this.layerControls = null
    this.loadingState = null
    this.currentMarker = null
    this.analytics = new Analytics()
    this.languageSwitcher = null
    this.lastAnalysisResult = null
    this._unsubscribeLanguage = null
  }

  async init() {
    await i18n.init()

    this.loadingState = new LoadingState(this.container)
    this.loadingState.show(i18n.t('common.loading'))

    try {
      await geoAnalyzer.init()
    } catch (error) {
      console.error('Failed to initialize geo analyzer:', error)
      this.loadingState.showError(i18n.t('errors.dataLoadFailed'))
      return
    }

    this.loadingState.hide()

    this.mapEngine = new MapEngine('map-container')
    this.mapEngine.init()
    this._initMapLayers()

    this.mapEngine.setPointSelectedCallback((latLng) => {
      this._handlePointSelected(latLng)
    })

    this.mapEngine.setMapClickCallback(() => {
      this.analysisPanel.close()
    })

    this._initUIComponents()
    this.analytics.init()

    this._unsubscribeLanguage = i18n.subscribe(() => {
      this._handleLanguageChanged()
    })
  }

  _initUIComponents() {
    this.layerControls = new LayerControls({
      onToggle: (layerType, visible) => {
        this.mapEngine.layerManager.toggleLayer(layerType, visible)
      }
    })
    this.layerControls.render(this.container)

    this.analysisPanel = new AnalysisPanel({
      onBookConsultation: (analysisData) => {
        this._openLeadFormModal(analysisData)
      }
    })
    this.analysisPanel.render(this.container)

    this.languageSwitcher = new LanguageSwitcher(this.container)
    this.languageSwitcher.render()
  }

  async _handlePointSelected(latLng) {
    const { lat, lng } = latLng

    if (this.currentMarker) {
      this.mapEngine.removeMarker(this.currentMarker)
    }
    this.currentMarker = this.mapEngine.addMarker([lat, lng])
    this.mapEngine.flyTo([lat, lng])

    this.analysisPanel.showLoading()

    try {
      const analysisResult = await geoAnalyzer.analyze(lat, lng)
      this.lastAnalysisResult = analysisResult

      const templateConfig = this._getTemplateConfig(analysisResult)
      const analysisText = templateEngine.generateAnalysis(
        analysisResult,
        templateConfig,
        i18n.getLanguage()
      )

      this.analysisPanel.showResult({
        analysisResult,
        analysisText,
        disclaimer: i18n.t('disclaimer.text')
      })

      this.analytics.trackMapClick({
        region: analysisResult.subzone?.nameZh,
        node: analysisResult.nearestNode?.node?.nameZh
      })
    } catch (error) {
      console.error('Analysis failed:', error)
      this.analysisPanel.showError(i18n.t('errors.analysisFailed'))
    }
  }

  _getTemplateConfig(analysisResult) {
    const templates = configStore.getTemplates()

    if (!templates) return null

    return templates
  }

  _openLeadFormModal(analysisData) {
    this.analytics.trackBookingClick({
      region: analysisData.analysisResult.subzone?.nameZh,
      node: analysisData.analysisResult.nearestNode?.node?.nameZh
    })

    const modal = new LeadFormModal({
      analysisData,
      onSubmit: (data) => {
        console.log('[LeadForm] submitted:', data)
        // TODO: 发送到后端 API
      }
    })
    modal.open()
  }

  _initMapLayers() {
    const elementToColor = (element) => {
      const colors = {
        木: '#4CAF50',
        火: '#F44336',
        土: '#795548',
        金: '#FFC107',
        水: '#2196F3'
      }
      return colors[element] || '#999999'
    }

    const regions = regionStore.getPlanningAreas() || regionStore.getAllRegions()
    if (regions) {
      this.mapEngine.layerManager.setRegionLayer(regions, (feature) => {
        const element = feature?.properties?.fengShui?.element
        return elementToColor(element)
      })
      this.mapEngine.layerManager.toggleLayer(LayerType.REGIONS, false)
    }

    const nodes = nodeStore.getAllNodes()
    if (nodes) {
      this.mapEngine.layerManager.clearLayer(LayerType.NODES)
      this.mapEngine.layerManager.addGeoJSON(LayerType.NODES, nodes, {
        fillColor: (feature) => elementToColor(feature?.properties?.element)
      })
    }

    const veins = veinStore.getAllVeins()
    if (veins) {
      this.mapEngine.layerManager.clearLayer(LayerType.VEINS)
      this.mapEngine.layerManager.addGeoJSON(LayerType.VEINS, veins, {
        color: (feature) => elementToColor(feature?.properties?.element),
        weight: 3,
        opacity: 0.8
      })
      this.mapEngine.layerManager.toggleLayer(LayerType.VEINS, false)
    }
  }

  _handleLanguageChanged() {
    if (this.layerControls) {
      this.layerControls.updateLanguage()
    }
    if (this.analysisPanel) {
      this.analysisPanel.updateLanguage()
    }

    if (this.lastAnalysisResult) {
      const templateConfig = this._getTemplateConfig(this.lastAnalysisResult)
      const analysisText = templateEngine.generateAnalysis(
        this.lastAnalysisResult,
        templateConfig,
        i18n.getLanguage()
      )
      this.analysisPanel.showResult({
        analysisResult: this.lastAnalysisResult,
        analysisText,
        disclaimer: i18n.t('disclaimer.text')
      })
    }
  }
}
